import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import AppScreenWrapper from '../../../components/app-shell/AppScreenWrapper';
import Sidebar from '../../../components/app-shell/Sidebar';
import ScreenOrbs from '../../../components/app-shell/ScreenOrbs';
import useClientData from '../../../hooks/useClientData';
import { CopyIcon, SendIcon, CaretIcon } from '../../../components/app-shell/icons';
import UROMark from '../../../components/app-shell/UROMark';

/* ─── docs nav structure — exact from docs.jsx design ──────────── */

const NAV = [
  { h: 'Getting Started', items: [{ l: 'Introduction' }, { l: 'Authentication' }, { l: 'Your first API call', a: true }, { l: 'Rate limits' }] },
  { h: 'Image Generation', items: [{ l: 'Run a model' }, { l: 'Poll for results' }, { l: 'Webhooks' }, { l: 'Available models' }] },
  { h: 'Video Generation', items: [{ l: 'Run a video model' }, { l: 'Supported models' }] },
  { h: 'LLM / Text',      items: [{ l: 'Chat completions' }, { l: 'Streaming' }, { l: 'Model list' }] },
  { h: 'Workflows',       items: [{ l: 'Run a workflow' }, { l: 'Workflow parameters' }] },
  { h: 'Brand DNA API',   items: [{ l: 'Overview' }, { l: 'Inject brand context' }] },
  { h: 'Reference',       items: [{ l: 'API reference' }, { l: 'Error codes' }, { l: 'SDKs' }] },
];

/* ─── syntax tokens — exact from docs.jsx design ──────────────── */

const TK = {
  k:  { color: '#FF8FB1' },
  s:  { color: '#FFC2A8' },
  v:  { color: '#A5B4FC' },
  fn: { color: '#7DD3FC' },
  n:  { color: '#FCD34D' },
  c:  { color: 'rgba(255,255,255,.32)', fontStyle: 'italic' },
  p:  { color: '#E5E7EB' },
  e:  { color: '#7DD3FC' },
  ' ': {},
};

const CURL_LINES = [
  [['k','curl'],[' ',' '],['p','-X POST '],['s','https://client-api.fluxuro.ai/api/models/run-model'],[' ',' '],['e','\\']],
  [['p','  -H '],['s','"x-api-key: YOUR_KEY"'],[' ',' '],['e','\\']],
  [['p','  -H '],['s','"Content-Type: application/json"'],[' ',' '],['e','\\']],
  [['p',"  -d '{"]], [],
  [['p','    '],['k','"model_id"'],['p',': '],['s','"nano-banana-flash"'],['p',',']],
  [['p','    '],['k','"positive_prompt"'],['p',': '],['s','"A product photo, white background, soft light"'],['p',',']],
  [['p','    '],['k','"aspect_ratio"'],['p',': '],['s','"1:1"']],
  [['p',"  }'"]],
];

const POLL_LINES = [
  [['k','const '],['v','poll'],['p',' = '],['k','async '],['fn','()'],['p',' => {']],
  [['p','  '],['k','const '],['v','r'],['p',' = '],['k','await '],['fn','fetch'],['p','('],['s','`https://client-api.fluxuro.ai/api/jobs/${id}`'],['p',')']],
  [['p','  '],['k','const '],['p','{ status, output } = '],['k','await '],['v','r'],['p','.'],['fn','json'],['p','()']],
  [['p','  '],['k','if '],['p','(status === '],['s','"complete"'],['p',') '],['k','return '],['v','output']],
  [['p','  '],['k','await '],['k','new '],['fn','Promise'],['p','(r => '],['fn','setTimeout'],['p','(r, '],['n','1500'],['p','))']],
  [['p','  '],['k','return '],['fn','poll'],['p','()']],
  [['p','}']],
];

function CodeCard({ label, lines }) {
  const [copied, setCopied] = useState(false);
  const copyCode = () => {
    navigator.clipboard.writeText(
      lines.map(r => r.map(([, v]) => v).join('')).join('\n')
    ).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };
  return (
    <div style={{ borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', background: 'rgba(0,0,0,.35)', marginTop: 16 }}>
      <div className="row" style={{ height: 36, padding: '0 14px', borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,.02)', justifyContent: 'space-between' }}>
        <span className="mono" style={{
          fontSize: 11, padding: '4px 10px', borderRadius: 6,
          background: 'rgba(99,102,241,.15)', color: '#A5B4FC',
          border: '1px solid rgba(99,102,241,.3)',
        }}>{label}</span>
        <button onClick={copyCode} style={{ background: 'transparent', border: 'none', color: copied ? '#6EE7B7' : 'var(--text-3)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
          <CopyIcon s={11} /> {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="mono" style={{ margin: 0, padding: 16, fontSize: 12.5, lineHeight: 1.7, color: '#E5E7EB', overflowX: 'auto' }}>
        {lines.map((row, i) => (
          <div key={i} style={{ display: 'flex' }}>
            <span style={{ color: 'rgba(255,255,255,.18)', width: 26, textAlign: 'right', paddingRight: 12, userSelect: 'none', flexShrink: 0 }}>{i + 1}</span>
            <span>{row.length === 0 ? ' ' : row.map(([k, v], j) => <span key={j} style={TK[k]}>{v}</span>)}</span>
          </div>
        ))}
      </pre>
    </div>
  );
}

export default function ApiDocsScreen() {
  const navigate = useNavigate();
  const { balance, clientName, clientEmail } = useClientData();
  const [activeNav, setActiveNav] = useState('Your first API call');
  const [playground, setPlayground] = useState({ prompt: 'A product photo, white background, soft light', model: 'nano-banana-flash', aspect: '1:1', response: null });

  return (
    <AppScreenWrapper>
      <div className="screen-root" style={{ display: 'flex' }}>
        <Sidebar balance={balance} name={clientName} email={clientEmail} />

        {/* Docs nav — exact from design */}
        <aside className="app-side-panel" style={{ width: 240, flex: '0 0 240px', height: '100%', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', background: 'rgba(255,255,255,.015)' }}>
          <div style={{ padding: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
            <UROMark size={20} />
            <span style={{ fontSize: 12, color: 'var(--text-3)', padding: '2px 8px', borderRadius: 999, border: '1px solid var(--border)' }}>Docs</span>
          </div>
          <div className="scroll-y" style={{ flex: 1, padding: '18px 14px 20px' }}>
            {NAV.map((sec, si) => (
              <div key={si} style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '.06em', textTransform: 'uppercase', padding: '0 8px', marginBottom: 6 }}>{sec.h}</div>
                {sec.items.map((it, i) => (
                  <a key={i} href="#" onClick={e => { e.preventDefault(); setActiveNav(it.l); }} style={{
                    display: 'block', padding: '6px 10px', borderRadius: 8,
                    fontSize: 13, fontWeight: it.l === activeNav ? 600 : 500,
                    color: it.l === activeNav ? '#A5B4FC' : 'var(--text-3)',
                    background: it.l === activeNav ? 'rgba(99,102,241,.08)' : 'transparent',
                    borderLeft: it.l === activeNav ? '2px solid #818CF8' : '2px solid transparent',
                    marginLeft: -2, paddingLeft: 12, textDecoration: 'none',
                  }}>{it.l}</a>
                ))}
              </div>
            ))}
          </div>
        </aside>

        {/* Main content — exact from design */}
        <div className="scroll-y" style={{ flex: 1, minWidth: 0 }}>
          <div style={{ maxWidth: 760, padding: '40px 48px 64px', margin: '0 auto' }}>
            <div style={{ marginBottom: 28, padding: 18, border: '1px solid var(--border)', borderRadius: 12 }}>
              <strong style={{ display: 'block', marginBottom: 6 }}>Create from your AI chat</strong>
              <p style={{ color: 'var(--text-2)', fontSize: 14, margin: '0 0 8px' }}>Use your URO library, images and video in Claude or ChatGPT.</p>
              <Link to="/connect" style={{ color: '#A5B4FC', fontSize: 14 }}>Connect Claude or ChatGPT</Link>
            </div>
            <div className="row" style={{ gap: 8, color: 'var(--text-3)', fontSize: 12 }}>
              <span>Getting Started</span><span style={{ color: 'var(--text-4)' }}>/</span>
              <span style={{ color: '#fff' }}>Your first API call</span>
            </div>
            <h1 style={{ marginTop: 12, fontSize: 38, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05 }}>Your first API call</h1>
            <p style={{ color: 'var(--text-2)', fontSize: 16, lineHeight: 1.65, marginTop: 14 }}>
              URO exposes a single REST surface for image, video and text generation. In three steps you'll be running real models against your account. Every request is authenticated with an API key; every output is priced per-result.
            </p>

            <h2 style={{ marginTop: 36, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>1. Get your API key</h2>
            <p style={{ color: 'var(--text-2)', fontSize: 14, lineHeight: 1.65, marginTop: 8 }}>
              Generate one in <a href="#" onClick={e => { e.preventDefault(); navigate('/account'); }} style={{ color: '#A5B4FC' }}>Account → API Keys</a>. Keys are prefixed <span className="mono" style={{ color: '#FFC2A8' }}>uro_live_…</span> and never expire unless revoked.
            </p>
            <div style={{ marginTop: 14, padding: 14, borderRadius: 10, background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.25)', fontSize: 13, color: 'var(--text-2)' }}>
              <strong style={{ color: '#A5B4FC' }}>Heads up:</strong> never expose your API key in client-side code.
            </div>

            <h2 style={{ marginTop: 36, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>2. Make your first request</h2>
            <p style={{ color: 'var(--text-2)', fontSize: 14, lineHeight: 1.65, marginTop: 8 }}>
              Submit a job by POSTing a <span className="mono">model_id</span> with its required parameters. The response contains a job id you'll poll for the result.
            </p>
            <CodeCard label="cURL" lines={CURL_LINES} />

            <h2 style={{ marginTop: 36, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>3. Poll for the result</h2>
            <p style={{ color: 'var(--text-2)', fontSize: 14, lineHeight: 1.65, marginTop: 8 }}>
              Polling is the simplest pattern. For higher throughput, register a webhook in Settings → Webhooks and skip polling entirely.
            </p>
            <CodeCard label="JavaScript" lines={POLL_LINES} />

            <h3 style={{ marginTop: 28, fontSize: 16, fontWeight: 700 }}>Response</h3>
            <div style={{ borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', background: 'rgba(0,0,0,.35)', marginTop: 10 }}>
              <div className="row" style={{ height: 32, padding: '0 12px', borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,.02)', justifyContent: 'space-between' }}>
                <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>application/json</span>
                <span className="chip" style={{ height: 20, fontSize: 10, background: 'rgba(16,185,129,.10)', borderColor: 'rgba(16,185,129,.3)', color: '#6EE7B7' }}>200 OK</span>
              </div>
              <pre className="mono" style={{ margin: 0, padding: 14, fontSize: 12.5, lineHeight: 1.7, color: '#E5E7EB' }}>{`{
  "image_job_id": "job_8f3a91b…",
  "status": "complete",
  "output": {
    "url": "https://cdn.fluxuro.ai/o/8f3a.jpg",
    "width": 1024,
    "height": 1024
  },
  "cost_usd": 0.002
}`}</pre>
            </div>

          </div>
        </div>

        {/* Live playground — exact from design */}
        <aside className="app-side-panel" style={{ width: 360, flex: '0 0 360px', height: '100%', borderLeft: '1px solid var(--border)', padding: 22, display: 'flex', flexDirection: 'column', gap: 12, background: 'rgba(255,255,255,.015)' }}>
          <ScreenOrbs tone="indigo" />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.01em' }}>Try it live</div>
              <span className="chip" style={{ height: 22, fontSize: 10, color: 'var(--text-3)' }}>
                Preview
              </span>
            </div>

            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 6 }}>API key</div>
              <div className="row" style={{ gap: 8 }}>
                <span className="mono" style={{ flex: 1, fontSize: 12, color: '#FFC2A8', overflow: 'hidden', textOverflow: 'ellipsis' }}>YOUR_API_KEY</span>
                <button title="Manage keys in Account" style={{ background: 'transparent', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }} onClick={() => navigate('/account')}><CopyIcon s={12} /></button>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 6 }}>Model</div>
              <div className="row" style={{ height: 38, padding: '0 12px', borderRadius: 10, gap: 10, background: 'rgba(255,255,255,.04)', border: '1px solid var(--border)', cursor: 'pointer' }}>
                <span style={{ width: 14, height: 14, borderRadius: 4, background: 'var(--grad)' }} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{playground.model}</span>
                <CaretIcon s={10} />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 6 }}>Positive prompt</div>
              <div className="card" style={{ padding: 12, minHeight: 80 }}>
                <textarea value={playground.prompt} onChange={e => setPlayground(p => ({ ...p, prompt: e.target.value }))}
                  style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 13, lineHeight: 1.5, resize: 'none', fontFamily: 'inherit', minHeight: 60 }}
                />
              </div>
            </div>

            <div className="row" style={{ gap: 8 }}>
              {[['Aspect', playground.aspect], ['Resolution', '1K']].map(([l, v]) => (
                <div key={l} style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 6 }}>{l}</div>
                  <div className="row" style={{ height: 34, padding: '0 10px', borderRadius: 8, background: 'rgba(255,255,255,.04)', border: '1px solid var(--border)', justifyContent: 'space-between', fontSize: 12 }}>
                    <span className="mono">{v}</span><CaretIcon s={10} />
                  </div>
                </div>
              ))}
            </div>

            <button className="btn btn-primary" disabled title="Coming soon" style={{ width: '100%', justifyContent: 'center', opacity: 0.5, cursor: 'not-allowed' }}>
              <SendIcon s={12} /> Send request (coming soon)
            </button>

            <div>
              <div className="row" style={{ justifyContent: 'space-between', marginTop: 8 }}>
                <span style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '.04em', textTransform: 'uppercase' }}>Example response</span>
              </div>
              <pre className="mono" style={{ margin: '6px 0 0', padding: 12, fontSize: 11.5, lineHeight: 1.6, background: 'rgba(0,0,0,.35)', border: '1px solid var(--border)', borderRadius: 10, color: '#E5E7EB', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                {`{\n  "image_job_id": "job_8f3a91b…",\n  "status": "complete",\n  "output": {\n    "url": "https://cdn.fluxuro.ai/o/…",\n    "width": 1024,\n    "height": 1024\n  },\n  "cost_usd": 0.002\n}`}
              </pre>            </div>
          </div>
        </aside>
      </div>
    </AppScreenWrapper>
  );
}
